import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'

import { ChainType } from '@lifi/types'
import { Account, useAccount, useAccountDisconnect, useWalletMenu } from '@lifi/wallet-management'

import { ChainId } from '@src/configs/chainInfo'
import { analyticsService } from '@src/services/analytics'
import { Address } from '@src/types/token'
import { validateEvmAddress } from '@src/utils/addressUtils'
import { createContextLogger, walletLogger } from '@src/utils/logger'
import { useApplicationStore } from '@src/zustand_stores'
import useLandingStore from '@src/zustand_stores/landing.store'

interface AuthProvider {
    account: Account | undefined
    accounts: Account[]
    isEVMConnected: boolean
    isSolanaConnected: boolean
    isTronConnected: boolean
    disconnectHandler: (account: Account) => void
    connectHandler: () => void
}

export const AuthProviderContext = createContext<AuthProvider>({
    account: undefined,
    accounts: [],
    isEVMConnected: false,
    isSolanaConnected: false,
    isTronConnected: false,
    // eslint-disable-next-line
    disconnectHandler: () => {},
    // eslint-disable-next-line
    connectHandler: () => {},
})

interface Props {
    children: React.ReactElement
}

const logger = createContextLogger('AuthProvider')

const useAuth = () => {
    const { account, accounts } = useAccount()
    const disconnectWallet = useAccountDisconnect()

    const { openWalletMenu } = useWalletMenu()

    const { setBoundReferralCode, setGeneratedReferralCode } = useApplicationStore()

    const setConnectedSign = useLandingStore(state => state.setConnectedSign)

    const isEVMConnected = useMemo(() => accounts?.some(acc => acc.chainType === ChainType.EVM), [accounts])

    // Check for Solana connections
    const isSolanaConnected = useMemo(() => accounts?.some(acc => acc.chainType === ChainType.SVM), [accounts])

    // Check for Tron connections
    const isTronConnected = useMemo(() => accounts?.some(acc => acc.chainId === ChainId.Tron), [accounts])

    // Track wallet connections
    useEffect(() => {
        if (accounts && accounts.length > 0) {
            accounts.forEach(acc => {
                if (acc.address && acc.chainType) {
                    // Include wallet name in the tracking event
                    // Since the Account type doesn't have a walletName property, we'll extract it from the connector
                    const walletName = acc.connector?.name
                    analyticsService.trackWalletConnect(acc.address, acc.chainType, walletName)
                    logger.info('Tracked wallet connection', {
                        address: acc.address,
                        chainType: acc.chainType,
                        walletName,
                    })
                }
            })
        }
    }, [accounts])

    const connectHandler = () => {
        // Track the main wallet connection attempt
        analyticsService.trackEvent('wallet_main_attempt_connect', {
            wallet_type: 'EVM/Solana',
            button_location: 'wallet_drawer', // This is from the wallet drawer
            timestamp: new Date().toISOString(),
        })

        // Open the wallet menu
        openWalletMenu()
    }

    const disconnectHandler = useCallback(
        (account: Account) => {
            try {
                setConnectedSign({ sign: '' })

                // Track wallet disconnection
                if (account.address && account.chainType) {
                    analyticsService.trackWalletDisconnect(account.address, account.chainType)
                    logger.info('Tracked wallet disconnection', {
                        address: account.address,
                        chainType: account.chainType,
                    })
                }

                // referral codes are only for EVM addresses
                if (account.address && validateEvmAddress(account.address)) {
                    setBoundReferralCode({ userAddress: '' as Address, code: '' })
                    setGeneratedReferralCode({ userAddress: '' as Address, code: '' })
                }

                logger.info('disconnecting account', account)

                disconnectWallet(account)
            } catch (err) {
                walletLogger.error('Failed to disconnect', { error: err })
            }
        },
        [setConnectedSign, setBoundReferralCode, setGeneratedReferralCode, disconnectWallet]
    )

    return {
        account,
        accounts,
        isEVMConnected,
        isSolanaConnected,
        isTronConnected,
        disconnectHandler,
        connectHandler,
    }
}

export const AuthProvider = (props: Props) => {
    const value = useAuth()
    return <AuthProviderContext.Provider value={value}>{props.children}</AuthProviderContext.Provider>
}

export const useAuthContext = () => {
    return useContext(AuthProviderContext)
}
